/**
 * Vorlage „In Scheiben schneiden“.
 *
 * Draufsicht auf das Brett. Das Messer schneidet von rechts nach links
 * gleichmäßig dicke Scheiben ab. Jede Scheibe kippt um und zeigt ihre
 * Schnittfläche; die Scheiben legen sich schuppenartig übereinander.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { compact, faded, group, line } from '../scene.ts';
import type { MaybeNode } from '../scene.ts';
import { crossCutDone, crossCutPose, knifeSquash, piece } from '../parts/cutting.ts';
import { board, knife } from '../parts/knife.ts';
import type { Pose } from '../parts/pose.ts';
import { crossSection, produce } from '../parts/produce.ts';
import type { ProduceKind } from '../parts/produce.ts';
import { arrow } from '../parts/stage.ts';
import { choice, defineTemplate, textValue } from '../template.ts';
import { ingredientFromText } from './cut-dice.ts';

/** Lage der Zutat. */
const BOX = { x: 34, y: 52, width: 98, height: 34 } as const;
const RIGHT = BOX.x + BOX.width;
const MID_Y = BOX.y + BOX.height / 2;
/** Beginn des Scheibenstapels. */
const PILE_X = RIGHT + 24;
/** Halbmesser einer umgefallenen Scheibe. */
const SLICE_R = BOX.height / 2 - 1;

const KNIFE_SCALE = 0.82;
const PARK: Pose = { x: 176, y: 128, rotate: -4, lift: 0.6 };

/** Zeitplan. */
const TIME = {
  appear: [0, 0.05],
  approach: [0.04, 0.1],
  cut: [0.1, 0.8],
  park: [0.8, 0.88],
  vanish: [0.93, 1],
} as const;

/** Scheibendicke je Stufe. */
const THICKNESS = { thin: 6, medium: 9, thick: 13 } as const;
type Thickness = keyof typeof THICKNESS;

/** Zutaten, die in Scheiben geschnitten werden. */
const ITEMS = { cucumber: 'Gurke', carrot: 'Möhre', potato: 'Kartoffel' } as const;

/** Zahl der Scheiben; ein Endstück bleibt übrig. */
function sliceCount(thickness: number): number {
  return Math.floor((BOX.width * 0.82) / thickness);
}

/** Vorlage „In Scheiben schneiden“. */
export const cutSliceTemplate = defineTemplate({
  id: 'cut.slice',
  group: 'cut',
  title: 'In Scheiben schneiden',
  description:
    'Die Zutat wird quer in gleichmäßig dicke Scheiben geschnitten. Die Scheiben kippen um und legen sich nebeneinander.',
  durationSeconds: 7,
  keywords: ['scheiben', 'in scheiben', 'aufschneiden', 'dünne scheiben', 'scheibchen', 'rädchen'],
  stillTime: 0.62,
  parameters: [
    choice('ingredient', 'Zutat', ITEMS, 'cucumber'),
    choice('thickness', 'Dicke', { thin: 'dünn', medium: 'mittel', thick: 'dick' }, 'medium'),
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    const found = ingredientFromText(text).ingredient;
    return {
      ingredient: found !== undefined && found in ITEMS ? found : undefined,
      thickness: /dünn|fein/.test(lower) ? 'thin' : /dick|grob/.test(lower) ? 'thick' : undefined,
    };
  },
  scene(values, t) {
    const kind = textValue(values, 'ingredient', 'cucumber') as ProduceKind;
    const thickness = THICKNESS[textValue(values, 'thickness', 'medium') as Thickness];
    const count = sliceCount(thickness);
    const item = produce(kind, BOX);
    const visible = Math.min(
      ease.outCubic(segment(t, ...TIME.appear)),
      1 - ease.inCubic(segment(t, ...TIME.vanish)),
    );
    const cutXs = Array.from({ length: count }, (_, index) => RIGHT - (index + 1) * thickness);
    const step = Math.min(thickness * 0.9, (220 - SLICE_R - PILE_X) / Math.max(1, count - 1));

    const slices: MaybeNode[] = [];
    let done = 0;
    for (let index = 0; index < count; index += 1) {
      const at = crossCutDone(index, count, TIME.cut);
      if (t < at) {
        break;
      }
      done = index + 1;
      const fall = ease.inOutCubic(segment(t, at, at + 0.06));
      const x0 = RIGHT - (index + 1) * thickness;
      const x1 = index === 0 ? RIGHT + 6 : RIGHT - index * thickness;
      const targetX = PILE_X + index * step;
      const targetY = MID_Y + (index % 2 === 0 ? -1.5 : 1.5);
      if (fall < 0.5) {
        // Die Scheibe löst sich und rutscht nach rechts.
        const cell = { x0, x1, y0: BOX.y - 6, y1: BOX.y + BOX.height + 6 };
        slices.push(piece(item, cell, fall * 2 * 6, 0, true));
      } else {
        // Umgefallen: Schnittfläche mit vollem Durchmesser.
        const f = (fall - 0.5) * 2;
        const cx = lerp((x0 + x1) / 2 + 6, targetX, f);
        slices.push(
          group(crossSection(kind, cx, lerp(MID_Y, targetY, f), SLICE_R), {
            transform: { scaleX: lerp(thickness / (SLICE_R * 2), 1, f), ox: cx, oy: MID_Y },
          }),
        );
      }
    }

    // Rest der Zutat mit der aktuellen Schnittkante.
    const restRight = RIGHT - done * thickness;
    const restCell = {
      x0: BOX.x - 12,
      x1: done === 0 ? RIGHT + 12 : restRight,
      y0: BOX.y - 12,
      y1: BOX.y + BOX.height + 12,
    };
    const cutEdge =
      done > 0
        ? group(
            [
              line(restRight, BOX.y - 4, restRight, BOX.y + BOX.height + 4, {
                stroke: 'ink',
                strokeWidth: STROKE.detail,
              }),
            ],
            {
              clip: item.outline,
            },
          )
        : null;
    const rest = group([piece(item, restCell, 0, 0, false), cutEdge]);
    return compact([
      ...board(),
      faded(visible, [rest, ...slices]),
      knife({
        ...crossCutPose(t, cutXs, BOX.y + BOX.height + 4, PARK, TIME),
        scale: KNIFE_SCALE,
        squash: knifeSquash(t, TIME),
      }),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: RIGHT - 4, y: BOX.y - 14 },
        { x: BOX.x + 30, y: BOX.y - 14 },
      ]),
      arrow([
        { x: RIGHT + 12, y: BOX.y + BOX.height + 8 },
        { x: PILE_X + 30, y: BOX.y + BOX.height + 8 },
      ]),
    ];
  },
});
